/**
 * Saving a value typed in a session (roadmap 1C, V-1 / V-6).
 *
 * One path for every session screen that takes a typed value:
 *
 * 1. It is saved as a contribution — a value_proposals row carrying this
 *    session's id. That is the act a Values Collector holds (values.propose).
 *    The contribution stays open, so someone who approves values can accept it
 *    as the workspace's official value from the item page.
 * 2. When the person may also accept values (values.accept), the same value
 *    becomes this session's value — accepted, scoped to this session only, and
 *    linked back to the contribution. It never becomes the official value here.
 *
 * Nothing is swallowed: a refusal comes back as an error the screen shows.
 */

import { useValueProposalStore } from '../stores/prioritization/valueProposalStore';
import type { AcceptanceMethod } from '../types/prioritization/itemLifecycle';

export interface SaveSessionValueInput {
  itemId: string;
  attributeKey: string;
  value: unknown;
  sessionId: string;
  sessionName?: string;
  trackId?: string | null;
  userId: string;
  userName?: string | null;
  role?: string | null;
  /** Whether the person holds values.accept. Without it, only the contribution is saved. */
  mayAcceptForSession: boolean;
  acceptanceMethod?: AcceptanceMethod;
  scaleId?: string | null;
}

export interface SaveSessionValueResult {
  /** The contribution was saved (value_proposals). */
  contributed: boolean;
  /** The value was also saved as this session's value (accepted, session scope). */
  savedForSession: boolean;
  /** Why something was not saved, in words a person can read. */
  error?: string;
}

export async function saveSessionValue(input: SaveSessionValueInput): Promise<SaveSessionValueResult> {
  const store = useValueProposalStore.getState();
  let contributionId: string;
  try {
    const contribution = await store.proposeValue({
      itemId: input.itemId,
      attributeKey: input.attributeKey,
      proposedValue: input.value,
      proposedBy: input.userId,
      proposedByName: input.userName ?? undefined,
      proposerRole: input.role ?? 'participant',
      trackId: input.trackId ?? undefined,
      scaleId: input.scaleId ?? null,
      sessionId: input.sessionId,
      source: 'session',
    });
    contributionId = contribution.id;
  } catch (err) {
    return {
      contributed: false,
      savedForSession: false,
      error: err instanceof Error ? err.message : 'Your value was not saved.',
    };
  }

  if (!input.mayAcceptForSession) {
    return { contributed: true, savedForSession: false };
  }

  try {
    const result = await store.acceptValueDirectly({
      itemId: input.itemId,
      attributeKey: input.attributeKey,
      value: input.value,
      acceptedBy: input.userId,
      acceptanceMethod: input.acceptanceMethod ?? 'facilitator_override',
      acceptanceNote: input.sessionName ? `Value entered in session ${input.sessionName}` : undefined,
      sessionScope: input.sessionId,
      scaleId: input.scaleId ?? undefined,
      acceptedFromProposalId: contributionId,
    });
    if (!result.saved) {
      return {
        contributed: true,
        savedForSession: false,
        error: `Saved as your contribution, but not as this session's value: ${result.error ?? 'unknown error'}`,
      };
    }
    return { contributed: true, savedForSession: true };
  } catch (err) {
    return {
      contributed: true,
      savedForSession: false,
      error: `Saved as your contribution, but not as this session's value: ${err instanceof Error ? err.message : 'refused'}`,
    };
  }
}
